/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type JSX, useCallback, useMemo } from 'react';
import { useLocation, useNavigation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@/hooks/use-navigate';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { getPaginationItems } from '@/lib/pagination-utils';
const DESKTOP_COLUMNS = 3; 

export default function CategoryPagination({
    limit,
    offset,
    total,
    limitOptions = [2, 4, 6, 8, 10, 12],
}: {
    limit: number;
    offset: number;
    total: number;
    limitOptions?: number[];
}): JSX.Element | null {
    const navigate = useNavigate();
    const location = useLocation();
    const navigation = useNavigation();
    const { t } = useTranslation('category');
    const isPending = navigation.state !== 'idle';

    /**
     * Optimistic offset derived from the in-flight navigation target.
     *
     * While a navigation is pending, `navigation.location` holds the target location, allowing us to read the
     * intended offset param immediately. Once the navigation settles, we fall back to the server-provided value.
     */
    const effectiveOffset = navigation.location
        ? Number(new URLSearchParams(navigation.location.search).get('offset') || offset)
        : offset;

    const effectiveLimit = navigation.location
        ? Number(new URLSearchParams(navigation.location.search).get('limit') || limit)
        : limit;
    const effectiveRows = Math.max(1, Math.round(effectiveLimit / DESKTOP_COLUMNS));

    const totalPages = Math.max(1, Math.ceil(total / effectiveLimit));
    const current = Math.max(1, Math.floor(effectiveOffset / effectiveLimit) + 1);
    const isPrevDisabled = current <= 1;
    const isNextDisabled = current >= totalPages;

    const pageNumbers = useMemo(() => getPaginationItems(totalPages, current), [totalPages, current]);

    const navigatePage = useCallback(
        (page: number) => {
            if (page < 1 || page > totalPages || page === current) {
                return;
            }
            const params = new URLSearchParams(location.search);
            params.set('offset', String((page - 1) * effectiveLimit));
            void navigate({
                ...location,
                search: `?${params.toString()}`,
            });
        },
        [location, navigate, effectiveLimit, totalPages, current]
    );

    const navigateLimit = useCallback(
        (newRows: string) => {
            const params = new URLSearchParams(location.search);
            params.set('limit', String(Number(newRows) * DESKTOP_COLUMNS));
            params.set('offset', '0');
            void navigate({
                ...location,
                search: `?${params.toString()}`,
            });
        },
        [location, navigate]
    );

    if (totalPages <= 1 && (!limitOptions || total <= Math.min(...limitOptions))) {
        return null;
    }

    return (
        <div className="flex items-center justify-center md:justify-between mb-3">
            {limitOptions && limitOptions.length > 0 && (
                <div className="md:flex items-center gap-2 text-sm text-foreground font-sans font-medium hidden">
                    <span
                        className="font-sans font-medium text-sm text-foreground leading-5 tracking-normal"
                        style={{ fontFamily: 'var(--font-sans, "Lato", sans-serif)', fontWeight: 500 }}>
                        {t('pagination.rowsPerPage', { defaultValue: 'Rows per page' })}
                    </span>
                    <select
                        value={effectiveRows}
                        onChange={(e) => navigateLimit(e.target.value)}
                        aria-label={t('pagination.rowsPerPage', { defaultValue: 'Rows per page' })}
                        id='plp-row-select'
                        style={{ fontFamily: 'var(--font-sans, "Lato", sans-serif)', fontWeight: 500 }}
                        className="font-sans font-medium h-9 w-[60px] rounded-md border border-input bg-background px-2 text-sm cursor-pointer">
                        {limitOptions.map((rows) => (
                            <option key={rows} value={rows} style={{ fontFamily: 'var(--font-sans, "Lato", sans-serif)', fontWeight: 500 }}>
                                {rows}
                            </option>
                        ))}
                    </select>
                </div>
            )}

            <nav
                className={`flex items-center gap-1 sm:gap-1.5 select-none${isPending ? ' pointer-events-none opacity-50 transition-opacity' : ''}`}
                aria-label={t('pagination.label', { defaultValue: 'Pagination' })}>
                <button
                    type="button"
                    className="plp-pagination-prev inline-flex items-center gap-1.5 px-2 py-1 text-[14px] max-md:text-[12px] font-normal !text-black transition-opacity hover:opacity-75 cursor-pointer mr-3 sm:mr-4"
                    onClick={() => {
                        if (!isPrevDisabled && !isPending) {
                            void navigatePage(current - 1);
                        }
                    }}
                    aria-label={t('pagination.previousPage', { defaultValue: 'Previous page' })}>
                    <ChevronLeft className="size-4 max-md:size-3.5 shrink-0 stroke-[1.5] !text-black" />
                    <span className="!text-black text-[14px] max-md:text-[12px]">{t('pagination.previous', { defaultValue: 'Previous' })}</span>
                </button>

                {pageNumbers.map((item) =>
                    typeof item === 'object' ? (
                        <span
                            key={`ellipsis-${item.key}`}
                            className="flex size-9 max-md:size-8 items-center justify-center text-[14px] max-md:text-[12px] font-normal !text-black select-none tracking-widest">
                            ···
                        </span>
                    ) : (
                        <button
                            key={item}
                            type="button"
                            onClick={() => {
                                if (!isPending) {
                                    void navigatePage(item);
                                }
                            }}
                            className={`flex size-9 max-md:size-8 items-center justify-center text-[14px] max-md:text-[12px] font-normal !text-black transition-opacity ${
                                current === item
                                    ? 'border border-[#D2D6DC] bg-white rounded-[2px]'
                                    : 'cursor-pointer hover:opacity-75'
                            }`}
                            aria-label={t('pagination.page', { number: item, defaultValue: `Page ${item}` })}
                            aria-current={current === item ? 'page' : undefined}>
                            {item}
                        </button>
                    )
                )}

                <button
                    type="button"
                    className="plp-pagination-next inline-flex items-center gap-1.5 px-2 py-1 text-[14px] max-md:text-[12px] font-normal !text-black transition-opacity hover:opacity-75 cursor-pointer ml-3 sm:ml-4"
                    onClick={() => {
                        if (!isNextDisabled && !isPending) {
                            void navigatePage(current + 1);
                        }
                    }}
                    aria-label={t('pagination.nextPage', { defaultValue: 'Next page' })}>
                    <span className="!text-black text-[14px] max-md:text-[12px]">{t('pagination.next', { defaultValue: 'Next' })}</span>
                    <ChevronRight className="size-4 max-md:size-3.5 shrink-0 stroke-[1.5] !text-black" />
                </button>
            </nav>
        </div>
    );
}
